/**
 * Board Service — カンバン UI のバックエンド API クライアント。
 * sessions API と同じ API Gateway（aws-exports.json の feedbackApiUrl 基点）の
 * /board 配下を呼ぶ。全エンドポイント Cognito ID トークン必須。
 */

let BOARD_API_URL = ""

async function loadApiUrl(): Promise<string> {
  if (BOARD_API_URL) return BOARD_API_URL
  const response = await fetch("/aws-exports.json")
  const config = await response.json()
  const baseUrl = config.feedbackApiUrl
  if (!baseUrl) throw new Error("API URL not found in aws-exports.json")
  BOARD_API_URL = `${baseUrl.replace(/\/?$/, "/")}board`
  return BOARD_API_URL
}

async function fetchWithAuth(path: string, idToken: string, options: RequestInit = {}) {
  const base = await loadApiUrl()
  const response = await fetch(`${base}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...(options.headers || {}),
    },
  })
  if (!response.ok) {
    let detail = ""
    try {
      const body = await response.json()
      detail = body.error || JSON.stringify(body)
    } catch {
      /* ignore */
    }
    throw new Error(detail || `Board API error: ${response.status}`)
  }
  return response.json()
}

export interface BoardTask {
  taskId: string
  title: string
  doneDef: string
  status: string
  phase: string
  notes?: string
  constraints?: string
  dueDate?: string
  askLevel?: number
  notifyTo?: string
  blocked?: boolean
  costUsd?: number
  // タスクは常に personal（company / group スコープは撤去・2026-07-05）。
  // 既存データに残る scope フィールドは読み捨てる（UI では未使用）。
  ownerSub?: string
  // 特殊タスク種別（memory_curation=記憶の手入れ。承認=適用 / 差し戻し=見送り）
  taskType?: string
  // ナレッジ還流の提案（バッチC）。worker が完了後に作る下書き。確認・編集して登録する。
  knowledgeProposal?: KnowledgeProposal
  createdAt: string
  updatedAt: string
  // ユーザー単位直列・テナント内最大10並列化（並列化バッチ）。
  // 「実行待ち」系で待機中のタスクのみ、自分の手前に何件あるかが入る。0/undefined は表示不要。
  aheadCount?: number
}

/** ナレッジ還流の登録提案（下書き）。フロントで本文を確認・編集して登録する。 */
export interface KnowledgeProposal {
  title: string
  description: string
  body_md: string
  recommendedScope: string
}

/** ログインユーザー情報（テナント・ロール・所属グループ。スコープ選択 UI 用） */
export interface BoardMe {
  email: string
  sub: string
  tenantId: string
  role: string
  groups: { groupId: string; name: string }[]
}

export const getMe = (idToken: string): Promise<BoardMe> => fetchWithAuth("/me", idToken)

// ─── これまでの歩み（Daily md・2026-07-02 P3）───
// エージェントが日々書き溜める日誌（本人の分のみ・サーバ側で sub 強制）

export const listDailyDates = (
  idToken: string,
  limit = 62
): Promise<{ dates: string[] }> => fetchWithAuth(`/daily?limit=${limit}`, idToken)

export const getDaily = (
  idToken: string,
  date: string
): Promise<{ date: string; markdown: string }> =>
  fetchWithAuth(`/daily/${date}`, idToken)

export interface ThreadEntry {
  taskId: string
  seq: number
  author: string
  type: string
  text: string
  ts: string
}

export interface Artifact {
  name: string
  url: string
  size: number
  // バッチG: ブラウザプレビュー。html=worker生成HTML / markdown / image / none
  previewKind?: "html" | "markdown" | "image" | "none"
  previewUrl?: string
}

export interface AgentProfile {
  agentName: string
  nameOrigin: string
  icon: string
  avatar: string
  avatarVer: string
  memory: string[]
  /** 振り返りメール（日次・月次ROI）を受け取るか。未設定はデフォルト ON */
  receiveReflectionEmail?: boolean
}

/** タスク一覧の1ページ（updatedAt 降順・最大200件）。nextCursor が null なら最終ページ。 */
export interface BoardTaskPage {
  tasks: BoardTask[]
  nextCursor: string | null
}

/** タスク一覧（1ページ）。cursor を渡すと続きのページを取得する。
 *  cursor はバックエンド発行の不透明トークン（base64 の LastEvaluatedKey）。 */
export const listTasks = (idToken: string, cursor?: string): Promise<BoardTaskPage> =>
  fetchWithAuth(cursor ? `/tasks?cursor=${encodeURIComponent(cursor)}` : "/tasks", idToken)

/** テナント内の実行中並列数（ユーザー単位直列・テナント内最大10並列化のバッジ用）。
 *  running=実行中の本数 / max=テナントの上限。 */
export interface QueueStatus {
  running: number
  max: number
}

export const getQueueStatus = (idToken: string): Promise<QueueStatus> =>
  fetchWithAuth("/queue-status", idToken)

export const registerTask = (
  idToken: string,
  task: {
    title: string
    done?: string // 下書き保存ではタイトルのみ必須（done は受付開始時に必須）
    notifyTo?: string
    notes?: string
    constraints?: string
    due?: string
    askLevel?: number
  }
): Promise<{ taskId: string }> =>
  fetchWithAuth("/tasks", idToken, { method: "POST", body: JSON.stringify(task) })

export const getThread = (idToken: string, taskId: string): Promise<ThreadEntry[]> =>
  fetchWithAuth(`/tasks/${taskId}/thread`, idToken)

export const getArtifacts = (idToken: string, taskId: string): Promise<Artifact[]> =>
  fetchWithAuth(`/tasks/${taskId}/artifacts`, idToken)

export const startIntake = (
  idToken: string,
  taskId: string,
  fill?: { done?: string; notifyTo?: string }
) =>
  fetchWithAuth(`/tasks/${taskId}/start`, idToken, {
    method: "POST",
    body: JSON.stringify(fill || {}),
  })

export const answerTask = (idToken: string, taskId: string, text: string) =>
  fetchWithAuth(`/tasks/${taskId}/answer`, idToken, {
    method: "POST",
    body: JSON.stringify({ text }),
  })

export const sendbackTask = (idToken: string, taskId: string, text: string) =>
  fetchWithAuth(`/tasks/${taskId}/sendback`, idToken, {
    method: "POST",
    body: JSON.stringify({ text }),
  })

/** 承認のレスポンス。完了の先の提案（バッチF）があれば nextStep を含む。 */
export interface NextStep {
  shouldSuggest: boolean
  title: string
  done_definition: string
  reason: string
}

export const approveTask = (
  idToken: string,
  taskId: string
): Promise<{ ok: boolean; nextStep?: NextStep }> =>
  fetchWithAuth(`/tasks/${taskId}/approve`, idToken, { method: "POST", body: "{}" })

/** ナレッジ還流の提案を承認して knowledge_store に登録する（バッチC）。
 *  編集後の本文(content)・名前(name)・一行説明(description)も送れる（省略時は提案値）。 */
export const applyKnowledge = (
  idToken: string,
  taskId: string,
  args: { scope: string; content?: string; name?: string; description?: string }
) =>
  fetchWithAuth(`/tasks/${taskId}/knowledge-apply`, idToken, {
    method: "POST",
    body: JSON.stringify(args),
  })

/** ナレッジ還流の提案を見送る（登録しない）。applyKnowledge のミラー。
 *  S3 へは何も保存せず、提案を消費済みにしてスレッドに記録するだけ。 */
export const dismissKnowledge = (idToken: string, taskId: string) =>
  fetchWithAuth(`/tasks/${taskId}/knowledge-dismiss`, idToken, {
    method: "POST",
    body: "{}",
  })

/** タスクを手動でブロック（中止）状態にする。理由は任意。 */
export const blockTask = (idToken: string, taskId: string, reason?: string) =>
  fetchWithAuth(`/tasks/${taskId}/block`, idToken, {
    method: "POST",
    body: JSON.stringify({ reason: reason || "" }),
  })

/** ブロックを解除して再投入する。 */
export const unblockTask = (idToken: string, taskId: string) =>
  fetchWithAuth(`/tasks/${taskId}/unblock`, idToken, { method: "POST", body: "{}" })

/** 添付アップロード: 署名付き URL を取得してクライアントから直接 S3 へ PUT する */
export async function uploadAttachment(idToken: string, taskId: string, file: File) {
  const { url } = await fetchWithAuth(`/tasks/${taskId}/attachment-url`, idToken, {
    method: "POST",
    body: JSON.stringify({ name: file.name }),
  })
  const put = await fetch(url, { method: "PUT", body: file })
  if (!put.ok) throw new Error(`アップロードに失敗しました (${put.status})`)
}

// ── ナレッジ v2（タイトル + 説明 + 複数ファイル） ──
// Phase 1 で deploy 済みの /board/knowledge/v2/* を叩く。1 ナレッジ＝
// 「タイトル + 説明 + 0..N ファイル」。v1（1 file = 1 ナレッジ）関数群は撤去した。

/** ナレッジタイトル配下の 1 ファイル（メタデータ）。実体は S3。 */
export interface KnowledgeFile {
  filename: string
  contentType?: string
  size: number
  uploadedAt?: string
}

/** ナレッジタイトル（束）。0..N ファイルと一行説明・enabled を持つ。
 *  参照トグルは二層: enabled（全員に効く・登録側の管理）と mutedByMe（自分の相棒
 *  からだけ外す個人設定）。実効は「全員 ON かつ 自分 ON」。canEdit=false の項目は
 *  閲覧モード（削除・編集不可・「自分」トグルのみ）。 */
export interface KnowledgeItem {
  ref?: string // バックエンド由来の "scope/titleId"（read 用・任意）
  titleId: string
  scope: string // "team" | "personal" | "groups/{groupId}"
  title: string
  description?: string
  enabled: boolean
  mutedByMe?: boolean // 自分がミュート中か（本人にだけ効く）
  canEdit?: boolean // 書き込みスコープ認可（無ければ true 扱い・後方互換）
  files: KnowledgeFile[]
  createdAt: string
  updatedAt: string
}

// ナレッジ登録のスコープ選択肢（全体・個人＋自分の所属グループ）。v1 から流用。
export interface KnowledgeScopes {
  team: boolean
  personal: boolean
  groups: { scope: string; groupId: string; name: string }[]
}

export const getKnowledgeScopes = (idToken: string): Promise<KnowledgeScopes> =>
  fetchWithAuth("/knowledge/scopes", idToken)

/** ナレッジタイトル一覧（管理 UI 用・OFF も含む）。 */
export const listKnowledge = (idToken: string): Promise<KnowledgeItem[]> =>
  fetchWithAuth("/knowledge/v2/titles", idToken)

/** 新しいナレッジタイトルを作成する（ファイルは後から uploadKnowledgeFile で足す）。 */
export const createKnowledgeTitle = (
  idToken: string,
  scope: string,
  title: string,
  description?: string
): Promise<{ titleId: string }> =>
  fetchWithAuth("/knowledge/v2/titles", idToken, {
    method: "POST",
    body: JSON.stringify({ scope, title, description: description || "" }),
  })

/** タイトルのメタを更新する。
 *  scope を指定した場合は S3 ファイルと index も新 scope へ移動する
 *  （サーバ側で旧 scope・新 scope 両方の書き込み権限を検証）。 */
export const updateKnowledgeTitle = (
  idToken: string,
  titleId: string,
  patch: { title?: string; description?: string; enabled?: boolean; scope?: string }
): Promise<KnowledgeItem> =>
  fetchWithAuth(`/knowledge/v2/titles/${titleId}`, idToken, {
    method: "PUT",
    body: JSON.stringify(patch),
  })

/** 「参照する/しない（自分）」— 自分の相棒にだけ効く個人ミュートを設定する。
 *  読めるスコープなら書き込み権限がなくても設定できる（全員フラグとは独立）。 */
export const setKnowledgeMuted = (
  idToken: string,
  titleId: string,
  muted: boolean
): Promise<{ ok: boolean }> =>
  fetchWithAuth(`/knowledge/v2/titles/${titleId}/mute`, idToken, {
    method: "PUT",
    body: JSON.stringify({ muted }),
  })

/** タイトルを完全削除する（配下のファイルも S3 から消える）。 */
export const deleteKnowledgeTitle = (
  idToken: string,
  titleId: string
): Promise<{ ok: boolean }> =>
  fetchWithAuth(`/knowledge/v2/titles/${titleId}`, idToken, { method: "DELETE" })

/** 個別ファイルをタイトルに追加する: upload-url 取得 → S3 PUT → metadata 登録。
 *  presigned PUT は署名時の ContentType と一致させる必要があるためヘッダを明示する。
 *  戻り値は更新後の KnowledgeItem。 */
export async function uploadKnowledgeFile(
  idToken: string,
  titleId: string,
  file: File
): Promise<KnowledgeItem> {
  if (file.size > 15 * 1024 * 1024) throw new Error("15MB を超えています")
  const contentType = file.type || "application/octet-stream"
  const { url, filename } = await fetchWithAuth(
    `/knowledge/v2/titles/${titleId}/upload-url`,
    idToken,
    { method: "POST", body: JSON.stringify({ filename: file.name, contentType }) }
  )
  const put = await fetch(url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": contentType },
  })
  if (!put.ok) throw new Error(`アップロードに失敗しました (${put.status})`)
  return fetchWithAuth(`/knowledge/v2/titles/${titleId}/files`, idToken, {
    method: "POST",
    body: JSON.stringify({ filename, contentType, size: file.size }),
  })
}

/** タイトル内の個別ファイルを削除する（タイトル自体は残す）。 */
export const deleteKnowledgeFile = (
  idToken: string,
  titleId: string,
  filename: string
): Promise<{ ok: boolean }> =>
  fetchWithAuth(
    `/knowledge/v2/titles/${titleId}/files/${encodeURIComponent(filename)}`,
    idToken,
    { method: "DELETE" }
  )

/** ナレッジ個別ファイルの presigned GET URL を取得する（プレビュー/ダウンロード両用）。
 *  返り値は PreviewModal の PreviewItem 互換 — Board 成果物と同じ UI で表示できる。 */
export interface KnowledgeFileUrl {
  name: string
  url: string
  /** ResponseContentDisposition=attachment 付き URL。cross-origin で <a download> が無視される
   *  問題を回避するための強制 DL 用。 */
  downloadUrl?: string
  previewKind: "markdown" | "image" | "pdf" | "html" | "none" | string
  previewUrl: string
}

export const getKnowledgeFileUrl = (
  idToken: string,
  titleId: string,
  filename: string
): Promise<KnowledgeFileUrl> =>
  fetchWithAuth(
    `/knowledge/v2/titles/${titleId}/files/${encodeURIComponent(filename)}/url`,
    idToken
  )

// ── 定期タスク（バッチE） ──
export interface Recurrence {
  recurrenceId: string
  title: string
  doneDef?: string
  notes?: string
  constraints?: string
  notifyTo?: string
  askLevel?: number
  ownerSub?: string
  spec: { freq: string; hour: number; minute: number; dayOfWeek?: number; dayOfMonth?: number }
  scheduleText?: string
  nextRunText?: string
  paused?: boolean
  lastRunDate?: string
}

export const listRecurrences = (idToken: string): Promise<Recurrence[]> =>
  fetchWithAuth("/recurrences", idToken)

export const createRecurrence = (
  idToken: string,
  body: {
    title: string
    done?: string
    notes?: string
    notifyTo?: string
    askLevel?: number
    freq: string
    hour: number
    minute: number
    dayOfWeek?: number
    dayOfMonth?: number
  }
): Promise<{ recurrenceId: string; scheduleText: string }> =>
  fetchWithAuth("/recurrences", idToken, { method: "POST", body: JSON.stringify(body) })

export const pauseRecurrence = (idToken: string, rid: string) =>
  fetchWithAuth(`/recurrences/${rid}/pause`, idToken, { method: "POST", body: "{}" })

export const resumeRecurrence = (idToken: string, rid: string) =>
  fetchWithAuth(`/recurrences/${rid}/resume`, idToken, { method: "POST", body: "{}" })

export const deleteRecurrence = (idToken: string, rid: string) =>
  fetchWithAuth(`/recurrences/${rid}`, idToken, { method: "DELETE" })

export const updateRecurrence = (
  idToken: string,
  rid: string,
  patch: Partial<{
    title: string
    done: string
    notes: string
    constraints: string
    notifyTo: string
    askLevel: number
    freq: string
    hour: number
    minute: number
    dayOfWeek: number
    dayOfMonth: number
  }>
): Promise<Recurrence> =>
  fetchWithAuth(`/recurrences/${rid}`, idToken, {
    method: "PUT",
    body: JSON.stringify(patch),
  })

export const getProfile = (idToken: string): Promise<AgentProfile> =>
  fetchWithAuth("/profile", idToken)

export const saveProfile = (
  idToken: string,
  updates: Partial<{
    agentName: string
    nameOrigin: string
    icon: string
    avatarType: string
    memorySet: string[]
    memoryAppend: string[]
    receiveReflectionEmail: boolean
  }>
) => fetchWithAuth("/profile", idToken, { method: "POST", body: JSON.stringify(updates) })

export const getAvatarUrl = (idToken: string): Promise<{ url: string }> =>
  fetchWithAuth("/profile/avatar-url", idToken)

export async function uploadAvatar(idToken: string, file: File) {
  if (file.size > 5 * 1024 * 1024) throw new Error("5MB 以下にしてください")
  const { url, contentType } = await fetchWithAuth("/profile/avatar-upload-url", idToken, {
    method: "POST",
    body: JSON.stringify({ name: file.name }),
  })
  const put = await fetch(url, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": contentType },
  })
  if (!put.ok) throw new Error(`アップロードに失敗しました (${put.status})`)
  await fetchWithAuth("/profile/avatar-complete", idToken, { method: "POST", body: "{}" })
}

export const tutorialChat = (
  idToken: string,
  agentName: string,
  messages: { role: string; text: string }[],
  mode: "onboarding" | "revisit" = "onboarding"
): Promise<{ text: string; done: boolean; facts?: string[] }> =>
  fetchWithAuth("/tutorial/chat", idToken, {
    method: "POST",
    body: JSON.stringify({ agentName, messages, mode }),
  })
